import type { PlatformToggleCounts } from '../../types/gotypes'
import { SPRINKLR_SOURCE, type SourceScope } from './platformFilter'

/**
 * The Source row (21 Oct 2026, design/2026-10-21-sky-sources-influencer-dynamic-frames.md):
 * WHERE a post came from in business terms — Sky's own accounts (Social),
 * creators' posts from Sky's influencer file (Influencer), later PR and
 * Internal. It sits under Platform and behaves exactly like it: one choice at a
 * time, All sends no constraint, and each button carries the posts and
 * comments pressing it would show.
 *
 * 🔴 NOT THE FETCH DRIVER. How a post's comments were fetched (Sprinklr for
 * Social, Apify for Influencer) is configuration, and a filter on it answered a
 * question nobody asks. The hidden Sprinklr/Apify control this replaces is
 * gone; the ingest pin it guarded lives on, fixed, below.
 */

/** The All button's value — never a real source. */
export const ALL_SOURCES_CHOICE = 'all'

/** Business sources in display order (goapi types.EntitySources). */
export const BUSINESS_SOURCES = ['social', 'influencer', 'pr', 'internal'] as const

/** Shown even before they hold anything: the two sources Sky has today. */
const ALWAYS_SHOWN = new Set<string>(['social', 'influencer'])

const SOURCE_LABELS: Record<string, string> = {
  social: 'Social',
  influencer: 'Influencer',
  pr: 'PR',
  internal: 'Internal',
}

export function businessSourceLabel(value: string): string {
  return SOURCE_LABELS[value] ?? value.replace(/\b\w/g, (c) => c.toUpperCase())
}

export interface BusinessSourceOption {
  value: string
  label: string
  /** Null until the counts answer: "not asked" must not read as zero. */
  videos: number | null
  comments: number | null
}

/**
 * The Source row's buttons. Social and Influencer always show (Sky has both);
 * PR and Internal appear once a post of theirs exists, so the row grows with
 * the data rather than offering an empty button. A source the server reports
 * that this list does not know still gets a button, after the known ones.
 */
export function deriveBusinessSourceOptions(counts?: PlatformToggleCounts): BusinessSourceOption[] {
  const videos = new Map((counts?.videos ?? []).map((v) => [v.value, v.count ?? 0]))
  const comments = new Map((counts?.comments ?? []).map((c) => [c.value, c.count ?? 0]))
  const known: string[] = [...BUSINESS_SOURCES]
  const values = [
    ...known.filter((s) => ALWAYS_SHOWN.has(s) || videos.has(s) || comments.has(s)),
    ...[...videos.keys(), ...comments.keys()].filter((s, i, all) => !known.includes(s) && all.indexOf(s) === i),
  ]
  return values.map((value) => ({
    value,
    label: businessSourceLabel(value),
    videos: counts ? (videos.get(value) ?? 0) : null,
    comments: counts ? (comments.get(value) ?? 0) : null,
  }))
}

/** The wire value for a choice: [] is "no constraint", which All must send. */
export function sourcesForQuery(choice: string): string[] {
  return choice === ALL_SOURCES_CHOICE || !choice ? [] : [choice]
}

/**
 * The record-level INGEST pin the selectable `all` view sends — what the
 * server forces on every department (productIngestSources): Sky's Sprinklr
 * comments, the comments fetched for the influencer file's posts, and X
 * replies read from X's own API (which replace Sprinklr's textless ones). The
 * legacy Apify archive stays out.
 */
export const INFLUENCER_INGEST_SOURCE = 'influencer'
export const X_API_INGEST_SOURCE = 'x'
export const DASHBOARD_INGEST_SCOPE: SourceScope = [SPRINKLR_SOURCE, INFLUENCER_INGEST_SOURCE, X_API_INGEST_SOURCE]
